import React, { useEffect, useMemo, useState } from 'react';
import { ordersAPI } from '../../services/api';
import { useAdmin } from '../../context/AdminContext';
import '../admin/AdminComponents.css';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const numberFormatter = new Intl.NumberFormat('en-US');

const sortMetricRows = (rows, sortBy, direction) => {
  const multiplier = direction === 'asc' ? 1 : -1;
  return [...(rows || [])].sort((a, b) => {
    if (sortBy === 'name') {
      return String(a.name || '').localeCompare(String(b.name || '')) * multiplier;
    }

    const aValue = Number(a[sortBy]) || 0;
    const bValue = Number(b[sortBy]) || 0;
    if (aValue === bValue) {
      return String(a.name || '').localeCompare(String(b.name || ''));
    }

    return (aValue - bValue) * multiplier;
  });
};

const SalesDataManagement = () => {
  const { textures: adminTextures } = useAdmin();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    sortBy: 'quantity',
    direction: 'desc',
    startDate: '',
    endDate: ''
  });
  const [textureMode, setTextureMode] = useState(false);

  const textureLookup = useMemo(() => {
    return (adminTextures || []).reduce((lookup, texture) => {
      lookup[texture.id] = texture;
      return lookup;
    }, {});
  }, [adminTextures]);

  useEffect(() => {
    loadAnalytics();
  }, [filters.status, filters.sortBy, filters.direction, filters.startDate, filters.endDate]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await ordersAPI.getAnalytics(filters);
      if (response.success) {
        setAnalytics(response.data);
      } else {
        setError(response.message || 'Failed to load sales data');
      }
    } catch (err) {
      console.error('Error loading sales data:', err);
      setError(err.message || 'Failed to load sales data');
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value
    }));
  };

  const getTextureName = (textureId, fallback) => {
    if (!textureId || textureId === 'default') return 'Default';
    return textureLookup[textureId]?.name || fallback || textureId;
  };

  const summary = analytics?.summary || {};
  const rows = analytics?.itemStats || [];

  return (
    <div className="admin-section sales-data-admin">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Sales Data</h2>
        <div className="admin-section-actions">
          <button className="admin-button" onClick={loadAnalytics} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="admin-error-message">{error}</div>
      )}

      <div className="sales-data-filters">
        <div className="admin-form-group">
          <label className="admin-form-label">Order Status</label>
          <select
            className="admin-form-select"
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            <option value="all">All Orders</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="paid">Paid</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="admin-form-group">
          <label className="admin-form-label">Sort By</label>
          <select
            className="admin-form-select"
            value={filters.sortBy}
            onChange={(e) => updateFilter('sortBy', e.target.value)}
          >
            <option value="quantity">Units Sold</option>
            <option value="revenue">Sales Value</option>
            <option value="orderCount">Order Count</option>
            <option value="averageValue">Average Item Value</option>
            <option value="name">Name</option>
          </select>
        </div>

        <div className="admin-form-group">
          <label className="admin-form-label">Direction</label>
          <select
            className="admin-form-select"
            value={filters.direction}
            onChange={(e) => updateFilter('direction', e.target.value)}
          >
            <option value="desc">Most First</option>
            <option value="asc">Least First</option>
          </select>
        </div>

        <div className="admin-form-group">
          <label className="admin-form-label">Start Date</label>
          <input
            className="admin-form-input"
            type="date"
            value={filters.startDate}
            onChange={(e) => updateFilter('startDate', e.target.value)}
          />
        </div>

        <div className="admin-form-group">
          <label className="admin-form-label">End Date</label>
          <input
            className="admin-form-input"
            type="date"
            value={filters.endDate}
            onChange={(e) => updateFilter('endDate', e.target.value)}
          />
        </div>
      </div>

      <label className="sales-data-toggle">
        <input
          type="checkbox"
          checked={textureMode}
          onChange={(e) => setTextureMode(e.target.checked)}
        />
        <span>Show texture performance per item</span>
      </label>

      {loading && !analytics ? (
        <div className="loading-message">Loading sales data...</div>
      ) : (
        <>
          <div className="sales-data-summary-grid">
            <div className="sales-data-card">
              <span>Total Orders</span>
              <strong>{numberFormatter.format(summary.totalOrders || 0)}</strong>
            </div>
            <div className="sales-data-card">
              <span>Total Sales Value</span>
              <strong>{currencyFormatter.format(summary.totalSalesValue || 0)}</strong>
            </div>
            <div className="sales-data-card">
              <span>Average Order Value</span>
              <strong>{currencyFormatter.format(summary.averageOrderValue || 0)}</strong>
            </div>
            <div className="sales-data-card">
              <span>Total Items Sold</span>
              <strong>{numberFormatter.format(summary.totalItemsSold || 0)}</strong>
            </div>
            <div className="sales-data-card">
              <span>Average Items Per Order</span>
              <strong>{(summary.averageItemsPerOrder || 0).toFixed(2)}</strong>
            </div>
          </div>

          <div className="sales-data-panel">
            <div className="sales-data-panel-header">
              <div>
                <h3>{textureMode ? 'Texture Performance By Item' : 'Item Sales Ranking'}</h3>
                <p>
                  {textureMode
                    ? 'Each item stays grouped, with its textures sorted inside the row.'
                    : `Sorted by ${filters.sortBy === 'quantity' ? 'units sold' : filters.sortBy === 'revenue' ? 'sales value' : filters.sortBy === 'orderCount' ? 'order count' : filters.sortBy}.`}
                </p>
              </div>
            </div>

            {rows.length === 0 ? (
              <div className="admin-empty-state">
                <p>No sales data found for the selected filters.</p>
              </div>
            ) : (
              <div className="admin-table-container">
                <table className="admin-table sales-data-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      {textureMode ? (
                        <>
                          <th>Best Texture</th>
                          <th>Texture Units</th>
                          <th>Texture Orders</th>
                          <th>Texture Sales Value</th>
                          <th>Texture Breakdown</th>
                        </>
                      ) : (
                        <>
                          <th>Units Sold</th>
                          <th>Orders</th>
                          <th>Sales Value</th>
                          <th>Average Unit Value</th>
                          <th>Top Textures</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const textureRows = sortMetricRows(row.textures || [], filters.sortBy, filters.direction);
                      const bestTexture = textureRows[0];
                      return (
                        <tr key={row.productId}>
                          <td>
                            <strong>{row.name}</strong>
                            <div className="sales-data-muted">
                              Product ID {row.productId}
                            </div>
                          </td>
                          {textureMode ? (
                            <>
                              <td>{bestTexture ? getTextureName(bestTexture.texture, bestTexture.name) : 'N/A'}</td>
                              <td>{numberFormatter.format(bestTexture?.quantity || 0)}</td>
                              <td>{numberFormatter.format(bestTexture?.orderCount || 0)}</td>
                              <td>{currencyFormatter.format(bestTexture?.revenue || 0)}</td>
                              <td>
                                <div className="sales-data-tags">
                                  {textureRows.map((texture) => (
                                    <span className="sales-data-tag" key={texture.texture}>
                                      {getTextureName(texture.texture, texture.name)} x{texture.quantity || 0}
                                      {' / '}
                                      {currencyFormatter.format(texture.revenue || 0)}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>{numberFormatter.format(row.quantity || 0)}</td>
                              <td>{numberFormatter.format(row.orderCount || 0)}</td>
                              <td>{currencyFormatter.format(row.revenue || 0)}</td>
                              <td>{currencyFormatter.format(row.averageValue || 0)}</td>
                              <td>
                                <div className="sales-data-tags">
                                  {textureRows.slice(0, 3).map((texture) => (
                                    <span className="sales-data-tag" key={texture.texture}>
                                      {getTextureName(texture.texture, texture.name)} x{texture.quantity || 0}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SalesDataManagement;
